/**
 * D359: a fully built world for the landing scene and the theme picker's previews. Pure: the
 * same theme, seed and time give the same world. Late in a game: every producer on the world
 * owned well past its milestones, some with hands, every chain built, the barn grown and a
 * mark for each festival, laid out by the seed as a new world of that theme would be (D344).
 * Looks only: nothing here is saved or sent.
 */
import { getIdleContent, openThemeIds } from '@hedgerow/data';
import { createIdleWorld, type IdleWorldState } from '@hedgerow/sim';
import { idleScene, type IdleScene } from './map.ts';

const DAY_MS = 86_400_000;

export function landingWorld(theme: string, seed: number, now: number): IdleWorldState {
  const content = getIdleContent(theme);
  // The theme rides on the state as the server's copy carries it (`themeOf`).
  const s = { ...createIdleWorld(content, now, seed >>> 0), theme } as IdleWorldState;
  const line = content.producers.filter((p) => p.land === undefined);
  line.forEach((p, i) => {
    s.year.producers[p.id] = {
      owned: Math.max(10, 120 - i * 10),
      hand: i % 3 === 0,
      running: true,
      progressMs: p.cycleMs * ((i * 0.37) % 1),
      cycleMs: p.cycleMs,
    };
  });
  s.year.number = 11;
  s.year.chains = content.chains.map((c) => c.id);
  s.permanent.barnLevel = 4;
  const marks = content.festival.marks;
  s.permanent.festivals = marks.map((mark, i) => ({
    year: i + 1,
    heritage: 10,
    mark,
    at: now - (marks.length - i) * DAY_MS,
  }));
  return s;
}

/** A seed and an open theme (D453) drawn at random for the landing scene. */
export function randomWorld(): { theme: string; seed: number } {
  const themes = openThemeIds();
  const theme = themes[Math.floor(Math.random() * themes.length)] ?? 'farm';
  return { theme, seed: Math.floor(Math.random() * 0xffffffff) };
}

/** The world as the maps draw it. */
export function landingScene(theme: string, seed: number, now: number): IdleScene {
  return idleScene(landingWorld(theme, seed, now), now);
}
